import { ProductNameVariant } from '../../../services/api/domains/products/types';

const MANUAL_LABEL = 'LGM Product Name';
const NONE_LABEL = 'None';

export interface PreviewSourceResult {
  /** The matching (or cascade-default) variant, if any — its name_id is the selected radio (Req 4). */
  variant: ProductNameVariant | null;
  /** Label for the Details tab's read-only "Sourced from" field (Req 5). */
  label: string;
}

/**
 * Shared name-comparison key: case- and whitespace-insensitive, so "Aspirin",
 * "aspirin", and "Aspirin  Tablet" (double space) all collapse to the same
 * key. Mirrors the R1 normalization convention used elsewhere in the
 * codebase (e.g. `normalize()` in api/constants/fuzzyLinkScoring.js —
 * `trim().replace(/\s+/g, ' ').toUpperCase()`), re-implemented client-side
 * since api/ and client/ are separate packages. Exported as the single
 * source of truth for both this function's text matching and the Names-tab
 * grouping in SynonymsRadioField.tsx (issue #622, Sub-task 10.1) — do not
 * duplicate this logic at the call site.
 */
export function normalizeName(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toUpperCase();
}

/**
 * Single source of truth for both the Names-tab derived radio selection
 * (Requirement 4, Decision 3) and the Details-tab "Sourced from" preview
 * (Requirement 5) — deliberately not duplicated between the two call sites.
 *
 * `variants` must already be in Requirement 3's response order (ranked
 * ascending, synonyms last) — this function never re-sorts, so the first
 * array match is always the lowest-rank match.
 */
export function resolvePreviewSource(
  preferredName: string | null | undefined,
  variants: ProductNameVariant[]
): PreviewSourceResult {
  const trimmed = (preferredName ?? '').trim();

  if (trimmed === '') {
    const firstRanked = variants.find(v => !v.is_synonym) ?? null;
    return {
      variant: firstRanked,
      label: firstRanked ? firstRanked.label : NONE_LABEL,
    };
  }

  const normalized = normalizeName(trimmed);
  const match =
    variants.find(v => normalizeName(v.name) === normalized) ?? null;

  return {
    variant: match,
    label: match ? match.label : MANUAL_LABEL,
  };
}
